import React from "react";
import ServicePage from "../../components/ServicePage";
import SurveillanceStats from "../../components/SurveillanceStats";

const ResidentialWiFiSurveillance: React.FC = () => (
  <ServicePage
    slug="residential-wifi-surveillance"
    title="Residential Wi-Fi Surveillance"
    metaDescription="Residential Wi-Fi surveillance in Central MA: video doorbells, wired and battery cameras, floodlight and spotlight cameras, and window and door alarms installed by J&S Audio Video Solutions. Free estimates."
    intro={
      <>
        Home surveillance has grown into one of our busiest services. Modern systems do far more than a doorbell
        camera -- wired and battery cameras, floodlights, spotlights, and window and door alarms can all work
        together as a single setup you manage from your phone. We come out, walk the property, and build the right
        coverage plan before anything gets installed.
        {" "}We install and support residential surveillance systems across Central Massachusetts and MetroWest, from Leicester and Worcester out to Milford and Framingham.
      </>
    }
    included={[
      {
        title: "Video Doorbells",
        body: "See and speak to whoever is at the door from anywhere. We handle the wiring, mounting, and app setup so it works the first time you need it.",
      },
      {
        title: "Wired and Battery Cameras",
        body: "Outdoor and indoor cameras selected for the location -- wired where you want reliability, battery-powered where running a wire isn't practical. Mounted cleanly and connected to your network.",
      },
      {
        title: "Floodlight and Spotlight Cameras",
        body: "Combined lighting and camera coverage for driveways, back yards, and detached garages. Motion-triggered so you see what triggered the light, not just the light itself.",
      },
      {
        title: "Window and Door Alarms",
        body: "Contact sensors on entry points that tie into the same app as your cameras, so any open door or window shows up alongside the live video feed.",
      },
      {
        title: "Home Wi-Fi coverage for cameras.",
        body: "Cameras are only as reliable as the network behind them. We test signal at every planned camera location before anything is mounted. If a spot is weak, we tell you exactly where and what the network needs — a stronger router placement, an extender, or a wired camera instead — so you fix it once rather than chasing dropped recordings.",
      },
    ]}
    afterIncluded={<SurveillanceStats />}
    processSteps={[
      { title: "Discover", body: "We walk the property with you — entry points, driveway, back yard, detached buildings, blind spots — and talk through what you actually want to see and be alerted to." },
      { title: "Design", body: "A coverage plan that names each camera location, wired vs. battery, and where the recording lives (local NVR or cloud), so there are no surprises on install day." },
      { title: "Install", body: "Clean mounting, concealed wiring wherever possible, Wi-Fi checked at every camera location, and the app configured on your phone before we leave." },
      { title: "Support", body: "If a camera drops offline or you add a doorbell later, you call us, not a 1-800 number." },
    ]}
    useCases={[
      {
        title: "New home purchase",
        body: "Moving in is the right time to get cameras placed correctly from the start, before furniture and landscaping get in the way.",
      },
      {
        title: "Existing system not covering enough",
        body: "Most camera systems have gaps -- a back door, a detached garage, a driveway blind spot. We walk the property and close them.",
      },
      {
        title: "Vacation or seasonal property",
        body: "Remote viewing and motion alerts so you know what is happening at a lake house or second home without being there.",
      },
      {
        title: "Upgrading from an old system",
        body: "Older wired systems that don't talk to a phone app are easy to replace. We'll tell you what's worth keeping and what isn't before quoting anything.",
      },
    ]}
    faqs={[
      {
        q: "Do you offer free estimates?",
        a: "Yes. We come out to the property, walk it with you, and put together a coverage plan before quoting. There's no charge for the visit.",
      },
      {
        q: "Do you work with Ring?",
        a: "Yes, Ring is one of the primary systems we install. They make far more than just doorbells -- wired and battery cameras, floodlight cameras, spotlight cameras, and window and door alarms all work in the same app. We're familiar with the full product line and can build out a complete system.",
      },
      {
        q: "Do the cameras need to be hardwired?",
        a: "Not necessarily. Battery-powered cameras are a solid option where running wire isn't practical. We'll recommend wired or wireless at each location based on what makes sense for reliability and coverage.",
      },
      {
        q: "Can I see the cameras on my phone?",
        a: "Yes. Remote viewing, motion alerts, and recorded clips from your phone are standard on every system we install. We set it up and walk you through it before we leave.",
      },
      {
        q: "How many cameras do I need?",
        a: "That depends on the property. We walk it with you and recommend coverage based on what you actually want to see -- entry points, driveways, blind spots -- not a fixed package.",
      },
      {
        q: "Can my cameras record audio in Massachusetts?",
        a: "Massachusetts requires consent from all parties to record a conversation (M.G.L. c. 272, § 99), and many doorbell and outdoor cameras record audio by default. We configure audio recording with that in mind and walk you through the settings on install day. For specifics about your situation, check with an attorney — we're installers, not lawyers.",
      },
    ]}
    serviceTypeSchema="Home Security Camera Installation"
    additionalTypeSchema="https://en.wikipedia.org/wiki/Closed-circuit_television"
    schemaDescription="Professional installation of home surveillance systems in Central Massachusetts, including video doorbells, wired and battery cameras, floodlight and spotlight cameras, and window and door alarms. Free property estimates."
  />
);

export default ResidentialWiFiSurveillance;
